import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../services/api';

const TrainingModulesPage = () => {
  const [modules, setModules] = useState([]);
  const [filteredModules, setFilteredModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState('all');
  const [availableYears, setAvailableYears] = useState([]);

  // Fetch training modules on component mount
  useEffect(() => {
    fetchTrainingModules();
  }, []);

  // Filter modules when year changes
  useEffect(() => {
    filterModulesByYear();
  }, [selectedYear, modules]);

  // Fetch training modules from API
  const fetchTrainingModules = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${api.BASE_URL}/public/docs`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API Response:', data);

      if (data && data.data && Array.isArray(data.data)) {
        // Filter only Training Modules
        const trainingModules = data.data.filter(
          doc => doc.category === 'Training Modules'
        );
        setModules(trainingModules);
        
        // Extract unique years from modules
        const years = [...new Set(trainingModules.map(m => m.year).filter(Boolean))].sort((a, b) => b - a);
        setAvailableYears(years);
        
        setError(null);
      } else {
        setError(data.message || 'Failed to fetch training modules');
      }
    } catch (err) {
      console.error('Error fetching training modules:', err);
      setError('Error fetching training modules: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Filter modules by selected year
  const filterModulesByYear = () => {
    if (selectedYear === 'all') {
      setFilteredModules(modules);
    } else {
      const filtered = modules.filter(module => module.year === selectedYear);
      setFilteredModules(filtered);
    }
  };

  // Handle year change
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
/*
  // Handle module view (open PDF in new tab)
  const handleView = (module) => {
    const url = `https://crd-training.in/uploads/${module.file}`;
    window.open(url, '_blank');
  };

  // Handle module download
  const handleDownload = (module) => {
    const url = `https://crd-training.in/uploads/${module.file}`;

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.src = url;
    document.body.appendChild(iframe);

    setTimeout(() => {
      try {
        document.body.removeChild(iframe);
      } catch (e) {
        // Ignore if already removed
      }
    }, 5000);

    setTimeout(() => {
      const link = document.createElement('a');
      link.href = url;
      link.download = module.name.endsWith('.pdf') ? module.name : `${module.name}.pdf`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, 1000);
  };
*/

const getModuleFileUrl = (filePath) => {
  if (!filePath) return '';

  const cleanPath = String(filePath).trim();

  if (
    cleanPath.startsWith('http://') ||
    cleanPath.startsWith('https://')
  ) {
    return cleanPath;
  }

  const normalizedPath = cleanPath.replace(/^\/+/, '');

  const encodedPath = normalizedPath
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');

  if (encodedPath.startsWith('uploads/')) {
    return `https://crd-training.in/${encodedPath}`;
  }

  if (encodedPath.startsWith('docs/')) {
    return `https://crd-training.in/uploads/${encodedPath}`;
  }

  return `https://crd-training.in/uploads/docs/${encodedPath}`;
};

const downloadFile = async (url, fileName) => {
  try {
    const response = await fetch(
      `${url}?v=${Date.now()}`,
      {
        cache: 'no-store'
      }
    );

    if (!response.ok) {
      alert('File not found on server');
      return;
    }

    const blob = await response.blob();

    if (blob.size < 1024) {
      alert(
        'Downloaded file is too small. It may be an error page, not the actual PDF.'
      );
      return;
    }

    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download error:', error);
    alert('Unable to download file');
  }
};

// Handle module view
const handleView = (module) => {
  if (!module?.file) {
    alert('File path is missing for this module');
    return;
  }

  const url = getModuleFileUrl(module.file);
  window.open(url, '_blank');
};

// Handle module download
const handleDownload = (module) => {
  if (!module?.file) {
    alert('File path is missing for this module');
    return;
  }

  const url = getModuleFileUrl(module.file);

  const fileName =
    module.file.split('/').pop() ||
    module.name ||
    'training-module.pdf';

  downloadFile(url, fileName);
};


  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-grow">
        {/* Hero Section */}
        <section className="relative py-10 bg-gradient-to-r from-blue-800 to-blue-600">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-2xl md:text-3xl font-bold text-white">Training Modules</h1>
              <div className="h-0.5 w-16 bg-white mt-2 mb-4"></div>
              <p className="text-white">
                Access comprehensive training materials and learning resources
              </p>
            </div>
          </div>
        </section>

        {/* Filter Section */}
        {!loading && !error && modules.length > 0 && (
          <section className="py-6 bg-white border-b border-gray-200">
            <div className="container mx-auto px-4">
              <div className="flex items-center justify-between max-w-4xl mx-auto">
                <div className="text-sm text-gray-600">
                  Showing <span className="font-semibold text-gray-900">{filteredModules.length}</span> of <span className="font-semibold text-gray-900">{modules.length}</span> modules
                </div>
                <div className="flex items-center gap-3">
                  <label htmlFor="year-filter" className="text-sm font-medium text-gray-700">
                    Filter by Year:
                  </label>
                  <select
                    id="year-filter"
                    value={selectedYear}
                    onChange={handleYearChange}
                    className="block rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm py-2 px-3"
                  >
                    <option value="all">All Years</option>
                    {availableYears.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Training Modules Grid Section */}
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4">
            {loading ? (
              <div className="py-20 text-center">
                <svg className="animate-spin h-12 w-12 text-blue-500 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <p className="text-gray-600 text-lg">Loading training modules...</p>
              </div>
            ) : error ? (
              <div className="py-20 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-red-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-red-600 mb-6 text-lg">{error}</p>
                <button
                  onClick={fetchTrainingModules}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Try Again
                </button>
              </div>
            ) : filteredModules.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredModules.map((module) => (
                  <div
                    key={module.id}
                    className="bg-white rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200"
                  >
                    {/* Module Thumbnail */}
                    <div className="relative h-48 bg-gradient-to-br from-blue-500 to-blue-700 overflow-hidden">
                      {module.thumbnail ? (
                        <img
                         // src={`https://crd-training.in/uploads/${module.thumbnail}`}
                         src={getModuleFileUrl(module.thumbnail)}
                          alt={module.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.classList.add('flex', 'items-center', 'justify-center');
                            const icon = document.createElement('div');
                            icon.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" class="h-20 w-20 text-white opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>';
                            e.target.parentElement.appendChild(icon.firstChild);
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-white opacity-80" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                      )}
                      <div className="absolute top-3 right-3 bg-white px-3 py-1 rounded-full">
                        <span className="text-xs font-semibold text-blue-600">{module.year || 'N/A'}</span>
                      </div>
                    </div>

                    {/* Module Content */}
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-gray-800 mb-2 line-clamp-2 min-h-[3.5rem]">
                        {module.name}
                      </h3>

                      <div className="flex items-center text-sm text-gray-500 mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {formatDate(module.date || module.created_at)}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleView(module)}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors duration-200"
                          title="View Module"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </button>
                        <button
                          onClick={() => handleDownload(module)}
                          className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md transition-colors duration-200"
                          title="Download Module"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Download
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="py-20 text-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <p className="text-gray-600 text-lg mb-2">
                  {selectedYear === 'all' ? 'No training modules available yet' : `No training modules found for ${selectedYear}`}
                </p>
                <p className="text-gray-500 text-sm">
                  {selectedYear !== 'all' && (
                    <button
                      onClick={() => setSelectedYear('all')}
                      className="text-blue-600 hover:text-blue-700 font-medium"
                    >
                      View all modules
                    </button>
                  )}
                </p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TrainingModulesPage;